import React, { useState, useEffect, memo } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  ChevronDownIcon,
  KeyIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

import EmployeeCard from "./EmployeeCard";
import FilterOptions from "./FilterOptions";
import CreateEmployeeForm from "./CreateEmployee";
import UpdatePasswordDialog from "./UpdatePasswordDailog";
import { 
  LogoutService, 
  GetEmployeesService,
  SearchEmployeesService 
} from "../services/ApiService";
import logo from "./logo2.png";

const MemoEmployeeCard = memo(EmployeeCard);

const BASE_URL = "https://www.code51.co";
// Custom debounce hook
function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const AdminDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    department: "all",
    status: "all",
    registration: "all",
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [openCardId, setOpenCardId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    document.title = "Admin Panel | Code 51";
  }, []);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onboardingComplete: 0,
    pendingOnboarding: 0,
  });

  const mapEmployeeData = (emp) => ({
    id: emp.id,
    name: emp.full_name,
    employeeId: emp.username,
    department: emp.department,
    role: emp.position,
    hireDate: emp.date_of_joining,
    registrationComplete: emp.is_onboarding_completed,
    photo: emp.profile_picture_url
      ? `${BASE_URL}${emp.profile_picture_url}`
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.full_name)}&background=10b981&color=fff&size=200`,
    email: emp.email,
    phone: emp.mobile_number,
    bloodGroup: emp.blood_group || "N/A",
    is_account_active: emp.is_account_active,
  });

  const calculateStats = (employeeList) => {
    const total = employeeList.length;
    const active = employeeList.filter((emp) => emp.is_account_active).length;
    const onboardingComplete = employeeList.filter(
      (emp) => emp.registrationComplete
    ).length;
    const pendingOnboarding = employeeList.filter(
      (emp) => !emp.registrationComplete
    ).length;

    setStats({
      total,
      active,
      onboardingComplete,
      pendingOnboarding,
    });
  };

const fetchEmployees = async (skip = 0, limit = 100) => {
  try {
    setLoading(true);
    const res = await GetEmployeesService(skip, limit);

    if (res && res.employees) {
      const mappedEmployees = res.employees.map((emp) => ({
        id: emp.id,
        name: emp.full_name,
        employeeId: emp.username,
        department: emp.department,
        role: emp.position,
        hireDate: emp.date_of_joining,
        registrationComplete: emp.is_onboarding_completed,
        photo: emp.profile_picture_url
          ? `${BASE_URL}${emp.profile_picture_url}`
          : `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.full_name)}&background=10b981&color=fff&size=200`,
        email: emp.email,
        phone: emp.mobile_number,
        bloodGroup: emp.blood_group || "N/A",
        is_account_active: emp.is_account_active,
      }));

      setEmployees(mappedEmployees);
      setFilteredEmployees(mappedEmployees); // ✅ ADD THIS LINE

      // Calculate statistics
      const total = mappedEmployees.length;
      const active = mappedEmployees.filter(
        (emp) => emp.is_account_active
      ).length;
      const onboardingComplete = mappedEmployees.filter(
        (emp) => emp.registrationComplete
      ).length;
      const pendingOnboarding = mappedEmployees.filter(
        (emp) => !emp.registrationComplete
      ).length;

      setStats({
        total,
        active,
        onboardingComplete,
        pendingOnboarding,
      });
    } else {
      setEmployees([]);
      setFilteredEmployees([]); // ✅ ADD THIS LINE TOO
      setStats({
        total: 0,
        active: 0,
        onboardingComplete: 0,
        pendingOnboarding: 0,
      });
    }

    setLoading(false);
  } catch (err) {
    console.error("Error fetching employees:", err);
    setEmployees([]);
    setFilteredEmployees([]); // ✅ AND THIS ONE
    setStats({
      total: 0,
      active: 0,
      onboardingComplete: 0,
      pendingOnboarding: 0,
    });
    setLoading(false);
  }
};


  const handleEmployeeUpdate = (employeeId, updatedData) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.id === employeeId ? { ...emp, ...updatedData } : emp
      )
    );

    setFilteredEmployees((prev) =>
      prev.map((emp) =>
        emp.id === employeeId ? { ...emp, ...updatedData } : emp
      )
    );
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Handle search with API
 // Handle search with API - Remove employees from dependency
useEffect(() => {
  const performSearch = async () => {
    if (debouncedSearchTerm.trim()) {
      try {
        setIsSearching(true);
        console.log("🔍 Searching for:", debouncedSearchTerm); // Debug log
        const results = await SearchEmployeesService(debouncedSearchTerm);
        console.log("✅ Search results:", results); // Debug log
        const mappedResults = results.map(mapEmployeeData);
        setFilteredEmployees(mappedResults);
      } catch (error) {
        console.error("❌ Search error:", error);
        // If 404 (no results found), set empty array
        if (error.message?.includes("No employees found") || error.message?.includes("404")) {
          setFilteredEmployees([]);
        } else {
          // For other errors, fallback to all employees
          setFilteredEmployees(employees);
        }
      } finally {
        setIsSearching(false);
      }
    } else {
      // If search is empty, show all employees
      setFilteredEmployees(employees);
    }
  };

  performSearch();
}, [debouncedSearchTerm]); // ✅ REMOVED employees from dependencies

// Apply additional filters (department, status, registration)
useEffect(() => {
  // Start with the base data
  let baseData = debouncedSearchTerm.trim() ? filteredEmployees : employees;
  let results = [...baseData]; // Create a copy

  if (filters.department !== "all") {
    results = results.filter(
      (employee) => employee.department === filters.department
    );
  }

  if (filters.status !== "all") {
    const now = new Date();
    const sixMonthsAgo = new Date(now.setMonth(now.getMonth() - 6));
    if (filters.status === "active") {
      results = results.filter(
        (employee) => new Date(employee.hireDate) > sixMonthsAgo
      );
    } else if (filters.status === "tenured") {
      results = results.filter(
        (employee) => new Date(employee.hireDate) <= sixMonthsAgo
      );
    }
  }

  if (filters.registration !== "all") {
    results = results.filter((employee) =>
      filters.registration === "complete"
        ? employee.registrationComplete
        : !employee.registrationComplete
    );
  }

  // Only update if filters are active OR search is empty
  if (filters.department !== "all" || filters.status !== "all" || filters.registration !== "all") {
    setFilteredEmployees(results);
  }
}, [filters, debouncedSearchTerm, employees, filteredEmployees]); // ✅ Added proper dependencies

  // Apply additional filters (department, status, registration)
 // Apply additional filters (department, status, registration)
  useEffect(() => {
    // 1. Determine base data:
    // If search is active, filter the SEARCH results. 
    // If search is empty, filter ALL employees.
    let baseData = employees; 
    
    // Note: Since search is handled by a separate API call in your code 
    // which updates 'filteredEmployees', we need to be careful not to overwrite it 
    // unless search is empty.
    
    // If search term exists, we assume 'filteredEmployees' currently holds the search results
    // so we filter those. If search is empty, we start fresh from 'employees'.
    if (debouncedSearchTerm.trim() !== "") {
       // We can't easily filter the search results again without losing the original search 
       // unless we stored 'searchResults' separately. 
       // For now, let's assume we filter 'employees' locally if search is empty,
       // BUT if search is active, this logic might conflict with server-side search.
       // A safer approach for client-side filtering:
       baseData = employees; 
    }

    // Create a copy to filter
    let results = [...baseData]; 

    // If search term is present, simplistic client-side filter to ensure it works with dropdowns
    // (If your Search API handles everything, you might skip this, but this makes the dropdowns work reliably)
    if (debouncedSearchTerm.trim()) {
        const lowerTerm = debouncedSearchTerm.toLowerCase();
        results = results.filter(emp => 
            emp.name.toLowerCase().includes(lowerTerm) || 
            emp.employeeId.toLowerCase().includes(lowerTerm) ||
            emp.department.toLowerCase().includes(lowerTerm)
        );
    }

    // 2. Filter by Department
    if (filters.department !== "all") {
      results = results.filter(
        (employee) => employee.department === filters.department
      );
    }

    // 3. Filter by Status (Active / Inactive) - FIXED LOGIC
    if (filters.status !== "all") {
        // Compare string "active" to boolean true/false
        const isActiveFilter = filters.status === "active"; 
        results = results.filter(
            (employee) => employee.is_account_active === isActiveFilter
        );
    }

    // 4. Filter by Registration (Complete / Incomplete)
    if (filters.registration !== "all") {
        const isCompleteFilter = filters.registration === "complete";
        results = results.filter(
            (employee) => employee.registrationComplete === isCompleteFilter
        );
    }

    // Update the displayed list
    setFilteredEmployees(results);

  }, [filters, employees, debouncedSearchTerm]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const resetFilters = () => {
    setFilters({ department: "all", status: "all", registration: "all" });
    setSearchTerm("");
  };

  const copyCredentials = async () => {
    const text = `Employee Code : ${credentials.employee_id}\nPassword : ${credentials.temporary_password}`;

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const downloadEmployeeData = (employee) => {
    const dataStr = JSON.stringify(employee, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${employee.employeeId}_data.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEmployeeCreate = ({ employee, credentials }) => {
    setEmployees((prev) => [...prev, employee]);
    if (credentials) {
      setCredentials(credentials);
      setShowCredentials(true);
    }

    setStats((prev) => ({
      ...prev,
      total: prev.total + 1,
      pendingOnboarding: employee.registrationComplete
        ? prev.pendingOnboarding
        : prev.pendingOnboarding + 1,
      onboardingComplete: employee.registrationComplete
        ? prev.onboardingComplete + 1
        : prev.onboardingComplete,
    }));
  };

  const handleToggle = (employeeId) => {
    setOpenCardId(openCardId === employeeId ? null : employeeId);
  };

  const handleLogout = async () => {
    try {
      await LogoutService();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* HEADER */}
      <header className="bg-white shadow-lg border-b border-emerald-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center py-4 gap-4">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src={logo}
                  alt="Company Logo"
                  className="h-16 w-44 object-contain"
                />
              </div>
              <div className="hidden md:block border-l border-emerald-200 pl-4">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Employee Management
                </h1>
                <p className="text-sm text-emerald-600 font-medium">
                  Admin Control Panel
                </p>
              </div>
            </div>

            {/* Search and Actions */}
            <div className="flex items-center space-x-3 w-full md:w-auto">
              <button
                onClick={() => setShowUpdatePassword(true)}
                className="flex items-center px-3 py-2 bg-white border border-emerald-300 text-emerald-700 text-sm font-medium rounded-lg hover:bg-emerald-50 hover:border-emerald-400 hover:shadow-sm transition-all duration-200"
              >
                <KeyIcon className="h-5 w-5 mr-1.5" />
                Password
              </button>

              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-green-700 hover:shadow-lg transition-all duration-200"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Employee
              </button>

              <button
                onClick={handleLogout}
                className="px-4 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-black hover:shadow-lg transition-all duration-200"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Mobile Title */}
          <div className="md:hidden mt-2 text-center">
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">
              Employee Management
            </h1>
            <p className="text-sm text-emerald-600 font-medium">
              Admin Control Panel
            </p>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Employees Card */}
          <div className="relative bg-white rounded-xl border border-emerald-100 shadow-sm p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
            <div className="absolute top-4 right-4">
              <div className="p-2 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <UserGroupIcon className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">
                Total Employees
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.total}
              </p>
              <div className="mt-4 flex items-center text-xs text-emerald-600">
                <div className="h-2 w-full bg-emerald-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
                    style={{
                      width: `${(stats.total / stats.total) * 100 || 0}%`,
                    }}
                  ></div>
                </div>
                <span className="ml-2">
                  {Math.round((stats.total / stats.total) * 100 || 0)}%
                </span>
              </div>
            </div>
          </div>

          {/* Active Accounts Card */}
          <div className="relative bg-white rounded-xl border border-emerald-100 shadow-sm p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
            <div className="absolute top-4 right-4">
              <div className="p-2 bg-gradient-to-br from-green-50 to-green-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">
                Active Accounts
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.active}
              </p>
              <div className="mt-4 flex items-center text-xs text-green-600">
                <div className="h-2 w-full bg-green-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full"
                    style={{
                      width: `${(stats.active / stats.total) * 100 || 0}%`,
                    }}
                  ></div>
                </div>
                <span className="ml-2">
                  {Math.round((stats.active / stats.total) * 100 || 0)}%
                </span>
              </div>
            </div>
          </div>

          {/* Onboarding Complete Card */}
          <div className="relative bg-white rounded-xl border border-emerald-100 shadow-sm p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
            <div className="absolute top-4 right-4">
              <div className="p-2 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">
                Onboarding Complete
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.onboardingComplete}
              </p>
              <div className="mt-4 flex items-center text-xs text-blue-600">
                <div className="h-2 w-full bg-blue-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"
                    style={{
                      width: `${(stats.onboardingComplete / stats.total) * 100 || 0}%`,
                    }}
                  ></div>
                </div>
                <span className="ml-2">
                  {Math.round(
                    (stats.onboardingComplete / stats.total) * 100 || 0
                  )}
                  %
                </span>
              </div>
            </div>
          </div>

          {/* Pending Onboarding Card */}
          <div className="relative bg-white rounded-xl border border-emerald-100 shadow-sm p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
            <div className="absolute top-4 right-4">
              <div className="p-2 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <ClockIcon className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">
                Pending Onboarding
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.pendingOnboarding}
              </p>
              <div className="mt-4 flex items-center text-xs text-yellow-600">
                <div className="h-2 w-full bg-yellow-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"
                    style={{
                      width: `${(stats.pendingOnboarding / stats.total) * 100 || 0}%`,
                    }}
                  ></div>
                </div>
                <span className="ml-2">
                  {Math.round(
                    (stats.pendingOnboarding / stats.total) * 100 || 0
                  )}
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl border border-emerald-100 shadow-sm p-6 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <FunnelIcon className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Employee Filters
                </h2>
                <p className="text-sm text-gray-600">
                  Refine your employee search
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg hover:bg-emerald-100 transition-all duration-200"
              >
                <ChevronDownIcon
                  className={`h-4 w-4 mr-2 transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`}
                />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                Reset All
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <FilterOptions
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </div>
          )}
        </div>

        {/* Results Summary with Search Bar */}
        <div className="bg-white rounded-xl border border-emerald-100 shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Left side - Title and count */}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <MagnifyingGlassIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Employee Directory
                  </h2>
                  <p className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold text-emerald-700">
                      {filteredEmployees.length}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-900">
                      {employees.length}
                    </span>{" "}
                    employees
                  </p>
                </div>
              </div>

              {filteredEmployees.length > 0 && (
                <div className="inline-flex items-center px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <span className="text-sm font-medium text-emerald-700">
                    {filteredEmployees.length} results found
                  </span>
                </div>
              )}
            </div>

            {/* Right side - Search bar */}
            <div className="w-full md:w-auto">
              <div className="relative">
                <div className="flex items-center space-x-4">
                  <div className="relative flex-1 md:w-80">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-emerald-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search by name, ID, department, or role..."
                      className="block w-full pl-10 pr-10 py-3 border-2 border-emerald-200 rounded-xl bg-white text-gray-900 placeholder-emerald-400 outline-none focus:outline-none"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {isSearching && (
                      <div className="absolute inset-y-0 right-10 pr-3 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
                      </div>
                    )}
                    {searchTerm && !isSearching && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-emerald-400 hover:text-emerald-600 transition-colors duration-200"
                      >
                        <span className="sr-only">Clear search</span>✕
                      </button>
                    )}
                  </div>

                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="px-4 py-3 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 hover:border-emerald-300 transition-all duration-200"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <p className="text-xs text-emerald-500 mt-2 ml-1">
                  {isSearching 
                    ? "Searching..." 
                    : "Search by name, employee ID, department, or role"
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Employee Cards Grid */}
        {loading ? (
          <div className="text-center py-16 bg-white rounded-xl border border-emerald-100 shadow-sm">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Loading employees...</p>
            </div>
          </div>
        ) : filteredEmployees.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredEmployees.map((employee) => (
              <MemoEmployeeCard
                key={employee.id}
                employee={employee}
                isOpen={openCardId === employee.id}
                onToggle={() => handleToggle(employee.id)}
                onDownload={() => downloadEmployeeData(employee)}
                onUpdate={handleEmployeeUpdate}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl border border-emerald-100 shadow-sm">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 mb-6">
              <MagnifyingGlassIcon className="h-10 w-10 text-emerald-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              No employees found
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              Try adjusting your search terms or filters to find what you're
              looking for.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={resetFilters}
                className="px-5 py-2.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-all duration-200"
              >
                Reset all filters
              </button>
              <button
                onClick={() => setSearchTerm("")}
                className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 hover:shadow-sm transition-all duration-200"
              >
                Clear search
              </button>
            </div>
          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="mt-12 border-t border-emerald-100 bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-3 mb-3">
                <img
                  src={logo}
                  alt="Company Logo"
                  className="h-10 w-28 object-contain"
                />
              </div>
              <p className="text-sm text-gray-600">
                © 2026 51 Code Solutions PVT. LTD. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* CREATE EMPLOYEE FORM MODAL */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border-2 border-emerald-100 shadow-2xl w-full max-w-2xl p-6 relative">
            <button
              onClick={() => setShowCreateForm(false)}
              className="absolute top-4 right-4 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 p-2 rounded-full transition-colors duration-200"
            >
              ✕
            </button>
            <CreateEmployeeForm
              onClose={() => setShowCreateForm(false)}
              onSubmit={handleEmployeeCreate}
            />
          </div>
        </div>
      )}

      {/* UPDATE PASSWORD DIALOG */}
      {showUpdatePassword && (
        <UpdatePasswordDialog
          isOpen={showUpdatePassword}
          onClose={() => setShowUpdatePassword(false)}
        />
      )}

      {/* CREDENTIALS MODAL */}
      {showCredentials && credentials && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl border-2 border-emerald-100 shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowCredentials(false)}
              className="absolute top-4 right-4 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 p-2 rounded-full transition-colors duration-200"
            >
              ✕
            </button>

            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg">
                <KeyIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Created Successfully
                </h3>
                <p className="text-sm text-emerald-600">
                  Save these credentials securely
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-900 to-gray-900 text-emerald-400 font-mono rounded-xl p-5 text-sm space-y-3 shadow-inner border border-emerald-800">
              <div className="flex items-center">
                <span className="text-emerald-300 mr-3">Employee Code:</span>
                <span className="font-bold tracking-wide bg-emerald-900/30 px-3 py-1 rounded">
                  {credentials.employee_id}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-emerald-300 mr-3">Password:</span>
                <span className="font-bold tracking-wide bg-emerald-900/30 px-3 py-1 rounded">
                  {credentials.temporary_password}
                </span>
              </div>
            </div>

            <button
              onClick={copyCredentials}
              className="mt-6 w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white py-3 rounded-xl font-medium hover:from-emerald-700 hover:to-green-700 hover:shadow-lg transition-all duration-200"
            >
              Copy Credentials to Clipboard
            </button>

            <p className="text-xs text-emerald-500 text-center mt-4">
              These credentials are only shown once. Please store them securely.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
